import type { CreateOrderAndOpenSocketConfig, PaymentConfig, PaymentContext } from "#/types/payment";

export type { CreateOrderAndOpenSocketConfig };

import axios from "axios";
import i18next from "i18next";
import { TOKEN_KEYS, useAuth } from "#/composables/useAuth";
import { bindSocketStatusReporting } from "#/sdk";
import { applyOutOfStockToCart, type OutOfStockItem } from "#/utils/applyOutOfStock";
import { InitConnection } from "../../requests";
import { useCartStore } from "../../store/cart";
import { useMainStore } from "../../store/main";
import { useTableStore } from "../../store/table";
import { useThemeStore } from "../../store/theme";
import { getWebSocketInstance } from "../../utils/WebSocketService";
import { PaymentMethodRegistry } from "../PaymentMethodRegistry";

// Глобальное хранилище для сокета и конфигурации (централизованное управление)
let globalWebSocket: any | null = null;
let globalConfig: PaymentConfig | null = null;
let globalWsUnsubscribers: Array<() => void> = [];

const disposeGlobalWebSocketListeners = () => {
	for (const unsub of globalWsUnsubscribers) unsub();
	globalWsUnsubscribers = [];
};

export function closeWebSocket(ws: any | null): void {
	if (ws === globalWebSocket) disposeGlobalWebSocketListeners();
	if (ws) {
		ws.close();
	}
}

export function resetPaymentSocket(): void {
	closeWebSocket(globalWebSocket);
	globalWebSocket = null;
	globalConfig = null;
}

/**
 * Инициализация платежной ссылки
 */
export async function initPaymentLink(orderId: string, method: string, extra: Record<string, unknown> = {}): Promise<any> {
	const storeCart = useCartStore();
	const storeMain = useMainStore();
	const tableStore = useTableStore();

	const connection = InitConnection.create();

	try {
		const response = await connection.initPaymentLink(orderId, method, extra);

		storeCart.paymentCostQR = storeMain.totalPrice_lib - storeMain.countBonus;
		storeCart.linkQR = response?.url;
		globalConfig?.deleteCart(null, globalConfig?.is_requestKiosk, true);

		return response;
	} catch (error: any) {
		if (error?.response?.data?.extra?.action === "DIFFERENT_DELIVERY_COST") tableStore.refresh();
		storeCart.isButtonDisabled = false;
		// @tolgee-key { key: 'payment.notification.create-order-error', ns: 'translation' }
		storeCart.showNotification(error?.response?.data?.user_friendly_message || i18next.t(`payment.notification.create-order-error`));
		throw error;
	}
}

/**
 * Обработчик сообщений WebSocket (базовая логика)
 */
export function baseWebSocketMessage(data: any, config: PaymentConfig, handleStatus: (data: any, config: PaymentConfig) => void): void {
	const themeStore = useThemeStore();
	const storeMain = useMainStore();

	if (!data?.params) return;
	const helpText = data.params.help_text || (data.params.status === "sent" ? themeStore.data?.kiosk_proceed_to_payment_text : null);
	if (helpText) config.showNotification(helpText);
	if (data.error) {
		config.showNotification(data.error);
		checkOrderStatus(config, null);
		checkCloseWebSocket(config);
		return;
	}
	if (data.method === "remove_out_of_stock_items") {
		const outOfStockItems: OutOfStockItem[] = data.params?.out_of_stock_items ?? [];

		applyOutOfStockToCart(storeMain.cartProducts_lib, outOfStockItems);
		storeMain.updateCartInLocalStorage();
	}
	handleStatus(data, config);
}

/**
 * Обработка RMS скидок
 */
export function handleRmsDiscounts(data: any, config: PaymentConfig): boolean {
	const storeCart = useCartStore();
	const storeMain = useMainStore();

	// if (!config.has_rms_discount) return false;

	if (data.params.new_discounts) {
		storeCart.extraDiscount = Number(data.params.new_total_cost) - (config as any).initialCartPrice;
		storeMain.totalPrice_lib = Number(data.params.new_total_cost);
		// @tolgee-key { key: 'payment.notification.discounts-applied', ns: 'translation' }
		config.showNotification(i18next.t(`payment.notification.discounts-applied`));
	}

	return true;
}

/**
 * Сохранение, восстановление и отчистка данных
 */
export const presistData = (withTokens: boolean = false, isAdultConfirmed: boolean = false) => {
	const { accessToken, refreshToken } = useAuth();
	if (localStorage.getItem("cart")) sessionStorage.setItem("_cart", localStorage.getItem("cart")!);
	if (localStorage.getItem("cart_comment")) sessionStorage.setItem("_cart_comment", localStorage.getItem("cart_comment")!);
	if (isAdultConfirmed) sessionStorage.setItem("_isAdultConfirmed", "true");

	if (withTokens) {
		if (accessToken.value) sessionStorage.setItem(`_${TOKEN_KEYS.ACCESS}`, accessToken.value);
		if (refreshToken.value) sessionStorage.setItem(`_${TOKEN_KEYS.REFRESH}`, refreshToken.value);
		if (sessionStorage.getItem("is_adult")) sessionStorage.setItem("_is_adult", sessionStorage.getItem("is_adult")!);
	}
};

export const clearSavedData = () => {
	sessionStorage.removeItem("_cart");
	sessionStorage.removeItem("_cart_comment");
	sessionStorage.removeItem(`_${TOKEN_KEYS.ACCESS}`);
	sessionStorage.removeItem(`_${TOKEN_KEYS.REFRESH}`);
	sessionStorage.removeItem("_is_adult");
	sessionStorage.removeItem("_isAdultConfirmed");
};

export const restoreCart = (config: PaymentConfig) => {
	if (sessionStorage.getItem("_cart")) {
		localStorage.setItem("cart", sessionStorage.getItem("_cart") as string);
		sessionStorage.removeItem("_cart");
	}

	if (sessionStorage.getItem("_cart_comment")) {
		useMainStore().setOrderComment(sessionStorage.getItem("_cart_comment") as string);
		sessionStorage.removeItem("_cart_comment");
	}

	const isAdultConfirmed = sessionStorage.getItem("_isAdultConfirmed") === "true";
	sessionStorage.removeItem("_isAdultConfirmed");

	checkLoadCart(config);

	return isAdultConfirmed;
};

export const restoreUserData = () => {
	const storeMain = useMainStore();
	const { setTokens } = useAuth();

	if (sessionStorage.getItem(`_${TOKEN_KEYS.ACCESS}`) && sessionStorage.getItem(`_${TOKEN_KEYS.REFRESH}`)) {
		setTokens({ access: sessionStorage.getItem(`_${TOKEN_KEYS.ACCESS}`) as string, refresh: sessionStorage.getItem(`_${TOKEN_KEYS.REFRESH}`) as string });
		sessionStorage.removeItem(`_${TOKEN_KEYS.ACCESS}`);
		sessionStorage.removeItem(`_${TOKEN_KEYS.REFRESH}`);
	}

	if (sessionStorage.getItem("_is_adult")) {
		const is_adult = sessionStorage.getItem("_is_adult") as string;
		sessionStorage.setItem("is_adult", is_adult);
		storeMain.is18Allowed = is_adult === "true";
		sessionStorage.removeItem("_is_adult");
	}
};

/**
 * Обработка смены экранов статусов
 */
export const checkOrderStatus = (config: PaymentConfig, status: number | null) => {
	if (config.updateOrderStatus) {
		config.updateOrderStatus(status);
	}
};

export const checkLoadCart = (config: PaymentConfig) => {
	if (config.loadCart) {
		config.loadCart();
	}
};

export const checkResetBonuses = (config: PaymentConfig) => {
	if (config.resetBonuses) {
		config.resetBonuses();
	}
};

export const checkResetDiscounts = (config: PaymentConfig) => {
	if (config.resetDiscounts) {
		config.resetDiscounts();
	}
};

export const checkCloseWebSocket = (config: PaymentConfig) => {
	if (config.closeWebSocket) {
		config.closeWebSocket();
	}
};

/**
 * Обработка оплаты заказа
 */
export const handleOrderPay = (data: any, config: PaymentConfig, ws: any, errorHandler: any): void => {
	if (data.params?.need_pay === false) return;
	// Используем ws из конфига, если доступен (централизованное управление), иначе используем переданный ws
	const wsToUse = config.ws || ws;
	const connection = InitConnection.create();
	connection
		.orderPay(data.params.order_id || "")
		.then((responsePay: any) => {
			if (config.setOrderNumber) {
				config.setOrderNumber(responsePay.data.order_number);
			}
			if (config.setOrderExternalId && responsePay.data.external_id) {
				config.setOrderExternalId(responsePay.data.external_id);
			}
		})
		.catch((e: any) => {
			const handler = config.errorHandler || errorHandler;
			handler.handleOrderPayError(e, { ...config, closeWebSocket: () => closeWebSocket(wsToUse) });
		});
};

/**
 * Обработка ошибок заказа
 */
export const handleOrderError = (data: any, config: PaymentConfig, ws: any, errorHandler: any): void => {
	const wsToUse = config.ws || ws;
	const handler = config.errorHandler || errorHandler;
	handler.handleOrderError(data, { ...config, closeWebSocket: () => closeWebSocket(wsToUse) });
};

/**
 * Определение правильного handler для метода оплаты
 * Использует selectedProvider если есть выбор, иначе первый доступный метод
 */
const getHandlerForPaymentMethod = (
	context: PaymentContext,
	is_requestKiosk: boolean,
	locator?: string,
	paymentMethodId?: string,
): { handler: any; config: PaymentConfig } | null => {
	const storeCart = useCartStore();
	const id = storeCart.getCurrentPaymentMethodId();
	if (!id) return null;

	const methodConfig = PaymentMethodRegistry.getConfig(id);
	if (!methodConfig || !PaymentMethodRegistry.isMethodAvailable(id, context)) return null;

	const handler = PaymentMethodRegistry.createHandler(id);
	if (!handler) return null;

	const config = storeCart.createPaymentHandler(context, is_requestKiosk, id, locator);
	if (!config) return null;

	return { handler, config: config.config };
};

/**
 * Обработка сообщений WebSocket с динамическим выбором handler
 * Всегда используется централизованное управление сокетом
 */
const handleWebSocketMessageWithDynamicHandler = (data: any): void => {
	if (!globalConfig) return;

	const context = globalConfig.context;
	const is_requestKiosk = globalConfig.is_requestKiosk;

	const handlerData = getHandlerForPaymentMethod(context, is_requestKiosk, globalConfig.locator, (globalConfig as any).paymentMethodId);
	if (!handlerData) {
		console.warn("handleWebSocketMessageWithDynamicHandler: не удалось получить handler");
		return;
	}

	const { handler, config } = handlerData;
	const configWithGlobal = { ...config, ...globalConfig };
	baseWebSocketMessage(data, configWithGlobal, handler.handleWebSocketStatus);
};

/**
 * Создание заказа и открытие WebSocket с полной обработкой ошибок и редиректов
 * handler определяется динамически при получении сообщений
 */
export async function createOrderAndOpenSocket(
	cartPayload: { items: any[]; combos: any[]; promo_code?: string },
	config: CreateOrderAndOpenSocketConfig,
	options: { comment: string; user: any; signal?: AbortSignal },
): Promise<any> {
	const tableStore = useTableStore();

	const { is_requestKiosk } = config;
	const { comment, user, signal } = options;
	config.setButtonDisabled(true);

	const connection = InitConnection.create();

	try {
		const response = await connection.createOrder(cartPayload, tableStore.id, is_requestKiosk, comment, user, signal, config.locator);

		if (response?.status === 201 && response?.data?.id) {
			const orderId = response.data.id;
			const configWithSignal = { ...config, signal };

			globalConfig = configWithSignal;

			const wsUrl = `${import.meta.env.VITE_WS_URL}/ws/order/${orderId}/`;
			disposeGlobalWebSocketListeners();
			globalWebSocket = getWebSocketInstance(wsUrl, { signal });

			const onOpen = () => {
				console.warn("WebSocket connection opened (centralized mode)");
			};

			const onError = (error: any) => {
				console.error("WebSocket error (centralized mode):", error);
				closeWebSocket(globalWebSocket);
				globalWebSocket = null;
				globalConfig = null;
				config.setButtonDisabled(false);
				config.errorHandler?.handlePaymentError(error, configWithSignal);
			};

			const onMessage = (data: any) => {
				handleWebSocketMessageWithDynamicHandler(data);
			};

			globalWsUnsubscribers = [
				bindSocketStatusReporting(globalWebSocket, "order", wsUrl),
				globalWebSocket.on("open", onOpen),
				globalWebSocket.on("message", onMessage),
				globalWebSocket.on("error", onError),
			];
			globalWebSocket.connect();

			globalConfig.ws = globalWebSocket;
			globalConfig.closeWebSocket = () => {
				closeWebSocket(globalWebSocket);
				globalWebSocket = null;
				globalConfig = null;
			};
		}

		return response;
	} catch (error: any) {
		if (axios.isCancel(error)) throw error;
		config.errorHandler?.handleCreateOrderError(error, config);
		config.setButtonDisabled(false);
		throw error;
	}
}
